import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Status from 'components/field/Status';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ORDER_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OrderOtomationForm.styled';

/**
 * @author @omerrkosar
 */

const ResultCard = ({ item, index }) => (
	<Accordion
		header={
			<S.AccordionHeaderContainer className="accordion-header" style={{ justifyContent: 'start' }}>
				<div style={{ display: 'flex', marginTop: '1rem', width: 'auto' }}>
					<Icon
						size="medium"
						icon={item?.success ? faCircleCheck : faCircleXmark}
						color={item?.success ? 'var(--green)' : 'var(--red)'}
					/>
					<Typography>{index + 1}</Typography>
				</div>
				<div className="mobile">
					<S.ResultColumns>
						<Typography>ruleName</Typography>
						<Typography noWrap fontWeight="700">
							{item?.ruleName}
						</Typography>
					</S.ResultColumns>
					<S.ResultColumns>
						<Typography>priorityMethod</Typography>
						<Typography noWrap fontWeight="700">
							{item?.priorityMethodType}
						</Typography>
					</S.ResultColumns>
					<S.ResultColumns>
						<Typography>status</Typography>
						<Status value={item?.status}></Status>
					</S.ResultColumns>
					<S.ResultColumns>
						<Typography>testResult</Typography>
						<Status value={item?.resultMessage}></Status>
					</S.ResultColumns>
				</div>
			</S.AccordionHeaderContainer>
		}
		style={{ width: '100%' }}
		defaultVisible>
		<S.AccordionContentContainer>
			<FormItem label="feasibility.conditions">
				{item?.orderConditionResultList?.length > 0 &&
					item.orderConditionResultList.map((condition, index) => (
						<div key={index} style={{ display: 'flex', gap: '1rem' }} className="mt-2">
							<Icon
								icon={condition?.result === true ? faCircleCheck : faCircleXmark}
								color={condition?.result === true ? 'var(--green)' : 'var(--red)'}
							/>
							<Typography noWrap fontWeight="700">
								{condition?.orderCondition.orderConditionType}
							</Typography>
							<Typography noWrap fontWeight="700">
								{condition?.orderCondition.operator}
							</Typography>
							<Typography noWrap fontWeight="700">
								{condition?.orderCondition.value}
							</Typography>
						</div>
					))}
			</FormItem>

			<FormItem label="locationFilters">
				{item?.locationFilterResultList?.length > 0 &&
					item?.locationFilterResultList.map((condition, index) => (
						<div key={index} style={{ display: 'flex', gap: '1rem' }} className="mt-2">
							<Icon
								icon={condition?.result === true ? faCircleCheck : faCircleXmark}
								color={condition?.result === true ? 'var(--green)' : 'var(--red)'}
							/>
							<Typography noWrap fontWeight="700">
								{condition?.locationFilterName}
							</Typography>
						</div>
					))}
			</FormItem>
			<FormItem label="locationResults">
				{item?.pickupLocationList?.length > 0 &&
					item.pickupLocationList.map((result, index) => (
						<div key={index} className="mt-2">
							<Typography fontWeight="700" noWrap color="var(--green)">
								{result}
							</Typography>
						</div>
					))}
			</FormItem>
		</S.AccordionContentContainer>
	</Accordion>
);
const OrderOtomationResultForm = ({ result }) => (
	<div style={{ width: '100%' }} className="resultContainer">
		<Typography className="p-2" variant="p1">
			results
		</Typography>

		<S.CardContainer>
			{result?.length > 0 ? (
				result?.map((data, i) => <ResultCard key={i} index={i} item={data} />)
			) : (
				<NoRecordsFound height="10rem"></NoRecordsFound>
			)}
		</S.CardContainer>
	</div>
);

const OrderOtomationTestForm = NiceModal.create(() => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderId: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const [isLoading, setIsLoading] = useState(false);
	const { showError } = useAlert();
	const [testResult, setTestResult] = useState(null);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderId: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (formData) => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(ORDER_AUTOMATION.GET_ORDER_AUTOMATION_TEST_RESULT, {
				otoId: formData.orderId
			});
			const { data, status } = response;

			if (status === 200) {
				setTestResult(data.data);
				setIsLoading(false);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (watch('orderId') === null || (watch('orderId') !== null && !isLoading)) {
			setTestResult(null);
		}
	}, [watch('orderId')]);

	const orderIdAutocompleteColumns = useMemo(() => [{ accessor: 'orderID', width: 200 }], []);

	return (
		<Modal fullwidth maxWidth="large">
			<ModalTitle>
				<Typography variant="p1">otomationTest</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderNo" required>
						<AutoComplete
							name="orderId"
							autoCompleteName={AUTOCOMPLETE.ORDERS}
							columns={orderIdAutocompleteColumns}
							selectionLabelAccessor={['orderID']}
							value={watch('orderId')}
							setValue={(value) => {
								setValue('orderId', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
								setTestResult(null);
							}}
						/>
					</FormItem>

					{testResult?.length >= 0 && <OrderOtomationResultForm result={testResult} />}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button variant="outline" label="cancel" onClick={() => modal.remove(OrderOtomationTestForm)} />
				<Button
					loading={isLoading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || testResult?.length >= 0}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="test"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OrderOtomationTestForm;
